import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  urlBackend,
} from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import {
  IPromocion,
  IPromocionDataPagingResponse,
} from '@/shared/interfaces/erp/promociones';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanSctack query

interface IAplicaPromocionParams {
  planes?: string
  provincias?: string
  ciudades?: string
  zonas?: string
  sectores?: string
  formas_de_pago?: string
  estado?: boolean
}

export const useFetchPromocion = ({
  params,
  enabled = false }:
  {
    params: IAplicaPromocionParams,
    enabled: boolean
  }) => {
  return useQuery({
    queryKey: ['promocion', ...Object.values(params || {})],
    queryFn: async () => {
      return await getAplicaPromocion(params);
    },
    enabled,
    retry: false,
  });
};

export const getAplicaPromocion = async (params?: IAplicaPromocionParams) => {
  const queryParams = getUrlParamsFetch(params || {})
  const resp = await get<IPromocionDataPagingResponse>(
    `${urlBackend}api/v1/promocion/?${queryParams}`,
    true
  );

  return resp.data.data
}

export const useFetchAllPromociones = (
  params?: GetPromocionesProps,
  enabled = true
) => {
  return useQuery({
    queryKey: ['promociones', ...Object.values(params ?? {})],
    queryFn: () => getPromociones(params),
    enabled: enabled,
  });
};

export const useGetPromocion = (id: string) => {
  return useQuery({
    queryKey: ['promocion', id],
    queryFn: () => getPromocion(id),
  });
};

export const useCreatePromocion = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (data: CreatePromocionProps) => createPromocion(data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promociones'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Promoción creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdatePromocion = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updatePromocion,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['promociones'],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Promoción actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeletePromocion = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: async (id: string) => removePromocion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promociones'] });
      setConfirmDialogIsOpen(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetPromocionesProps = Partial<IPromocion> & {
  page?: number;
  page_size?: number;
  filterByState?: boolean;
};
export type CreatePromocionProps = Omit<IPromocion, 'id_promocion'>;
export type UpdatePromocionProps = {
  id: string;
  data: CreatePromocionProps;
};

export const getPromociones = async (params?: GetPromocionesProps) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<IPromocionDataPagingResponse>(
    `${urlBackend}api/v1/promocion/?${queryParams}`,
    true
  );
};

export const getPromocion = async (id: string) => {
  try {
    return await get<IPromocion>(`${urlBackend}api/v1/promocion/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createPromocion = async (data: CreatePromocionProps) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<IPromocion>(`${urlBackend}api/v1/promocion/`, data, true);
};

export const updatePromocion = async ({ data, id }: UpdatePromocionProps) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<IPromocion>(`${urlBackend}api/v1/promocion/${id}/`, data, true);
};

export const removePromocion = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  const res = await remove<IPromocion>(
    `${urlBackend}api/v1/promocion/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
